import React, { useState, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Upload, Download, FileText, AlertCircle, CheckCircle2, X, RefreshCw } from 'lucide-react';
import { bulkMappingImport } from '../lib/api';
import { cn } from '../lib/utils';
import logger from '../lib/logger';
import * as XLSX from 'xlsx';

// Bulk mapping CSV/XLSX import dialog.
// Accepts: item_name, recipe_name OR recipe_id, plus optional item_id, item_category.
// Operator chooses Preview (dry-run) or Import. auto_publish is opt-in.
// No silent overwrites — server returns per-row outcomes.

const TEMPLATE_HEADERS = ['item_name', 'recipe_name', 'recipe_id', 'item_id', 'item_category'];

const SAMPLE_ROWS = [
  { item_name: 'Eggs Benedict', recipe_name: 'Eggs Benedict', recipe_id: '', item_id: '', item_category: 'Breakfast' },
  { item_name: 'Cold Coffee', recipe_name: 'Cold Coffee', recipe_id: '', item_id: '', item_category: 'Beverages' },
  { item_name: 'Avocado Toast', recipe_name: '', recipe_id: 'paste-recipe-id-here-if-name-ambiguous', item_id: '', item_category: '' },
];

const STATUS_TONE = {
  APPLIED: { tone: 'emerald', icon: CheckCircle2, label: 'Mapped' },
  PUBLISHED_AND_APPLIED: { tone: 'amber', icon: CheckCircle2, label: 'Published & Mapped' },
  ALREADY_MAPPED_SAME_RECIPE: { tone: 'zinc', icon: CheckCircle2, label: 'Already mapped (no change)' },
  ALREADY_MAPPED_DIFFERENT_RECIPE: { tone: 'red', icon: AlertCircle, label: 'Conflict — different recipe' },
  DRAFT_RECIPE_BLOCKED: { tone: 'amber', icon: AlertCircle, label: 'Draft (enable auto-publish)' },
  RECIPE_NOT_FOUND: { tone: 'red', icon: AlertCircle, label: 'Recipe not found' },
  AMBIGUOUS_RECIPE_NAME: { tone: 'red', icon: AlertCircle, label: 'Ambiguous name' },
  RECIPE_ARCHIVED: { tone: 'red', icon: AlertCircle, label: 'Recipe archived' },
  RECIPE_NOT_PUBLISHABLE: { tone: 'red', icon: AlertCircle, label: 'Not publishable (see message)' },
  INVALID_ROW: { tone: 'red', icon: AlertCircle, label: 'Invalid row' },
};

const TONE_CLASSES = {
  emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  red: 'bg-red-500/10 text-red-300 border-red-500/30',
  zinc: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/30',
};

function parseCsv(text) {
  // Light CSV: handles quoted commas. Sufficient for typical export files.
  const rows = [];
  let row = [], cell = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], next = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') { cell += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else cell += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') { row.push(cell); cell = ''; }
      else if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
      else if (ch === '\r') { /* skip */ }
      else cell += ch;
    }
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((v) => v && v.trim() !== ''));
}

function rowsFromMatrix(matrix) {
  if (!matrix.length) return { headers: [], rows: [] };
  const headers = matrix[0].map((h) => (h || '').trim().toLowerCase());
  const rows = matrix.slice(1).map((vals) => {
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').toString().trim(); });
    return obj;
  });
  return { headers, rows };
}

const BulkMappingImportDialog = ({ open, onClose, onComplete, outletId }) => {
  const [parsedRows, setParsedRows] = useState([]);   // [{item_name, recipe_name, recipe_id, ...}]
  const [headers, setHeaders] = useState([]);
  const [autoPublish, setAutoPublish] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);  // dry_run output
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);     // final import output
  const fileInputRef = useRef(null);

  const handleClose = () => {
    setParsedRows([]); setHeaders([]); setAutoPublish(false);
    setPreviewResult(null); setImportResult(null); setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose?.();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      let matrix;
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        const wb = XLSX.read(buf, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      } else {
        const text = new TextDecoder('utf-8').decode(buf);
        matrix = parseCsv(text);
      }
      const { headers: h, rows } = rowsFromMatrix(matrix);
      const required = ['item_name'];
      const missing = required.filter((r) => !h.includes(r));
      if (missing.length) {
        toast.error(`Missing required columns: ${missing.join(', ')}`);
        return;
      }
      if (!h.includes('recipe_name') && !h.includes('recipe_id')) {
        toast.error('File must contain at least one of: recipe_name, recipe_id');
        return;
      }
      setHeaders(h);
      setParsedRows(rows);
      setPreviewResult(null);
      setImportResult(null);
      toast.success(`Parsed ${rows.length} rows`);
    } catch (err) {
      logger.error('[BulkMappingImport] parse error:', err);
      toast.error('Failed to parse file');
    }
  };

  const downloadTemplate = (kind = 'csv') => {
    if (kind === 'csv') {
      const lines = [TEMPLATE_HEADERS.join(',')];
      SAMPLE_ROWS.forEach((r) => {
        lines.push(TEMPLATE_HEADERS.map((h) => {
          const v = (r[h] || '').toString();
          return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
        }).join(','));
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'sales_mapping_template.csv'; a.click();
      URL.revokeObjectURL(url);
    } else {
      const ws = XLSX.utils.json_to_sheet(SAMPLE_ROWS, { header: TEMPLATE_HEADERS });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'mappings');
      XLSX.writeFile(wb, 'sales_mapping_template.xlsx');
    }
  };

  const runPreview = async () => {
    if (!parsedRows.length) return;
    setImporting(true);
    try {
      const res = await bulkMappingImport({
        outlet_id: outletId,
        rows: parsedRows,
        auto_publish: autoPublish,
        dry_run: true,
      });
      setPreviewResult(res);
    } catch (err) {
      logger.error('[BulkMappingImport] preview error:', err);
      toast.error('Preview failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const runImport = async () => {
    if (!parsedRows.length) return;
    setImporting(true);
    try {
      const res = await bulkMappingImport({
        outlet_id: outletId,
        rows: parsedRows,
        auto_publish: autoPublish,
        dry_run: false,
      });
      setImportResult(res);
      const s = res.summary || {};
      const ok = (s.applied || 0) + (s.published_and_applied || 0);
      const skipped = s.skipped_existing || 0;
      const errors = (s.invalid || 0) + (s.missing_recipe || 0) + (s.ambiguous || 0)
                   + (s.already_mapped_other || 0) + (s.draft_blocked || 0) + (s.archived || 0);
      if (errors > 0) {
        toast.warning(`Imported ${ok} mappings; ${errors} rows had issues. Review the results.`);
      } else {
        toast.success(`Imported ${ok} mappings (${skipped} no-op).`);
      }
    } catch (err) {
      logger.error('[BulkMappingImport] import error:', err);
      toast.error('Import failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const summary = importResult?.summary || previewResult?.summary || null;
  const result = importResult || previewResult;

  const summaryRows = useMemo(() => {
    if (!summary) return [];
    return [
      { label: 'Total rows', value: summary.total, tone: 'zinc' },
      { label: 'Mapped', value: summary.applied, tone: 'emerald' },
      { label: 'Published & mapped', value: summary.published_and_applied, tone: 'amber' },
      { label: 'Already mapped (skip)', value: summary.skipped_existing, tone: 'zinc' },
      { label: 'Conflicts', value: summary.already_mapped_other, tone: 'red' },
      { label: 'Draft blocked', value: summary.draft_blocked, tone: 'amber' },
      { label: 'Recipe not found', value: summary.missing_recipe, tone: 'red' },
      { label: 'Ambiguous name', value: summary.ambiguous, tone: 'red' },
      { label: 'Archived recipe', value: summary.archived, tone: 'red' },
      { label: 'Not publishable', value: summary.not_publishable, tone: 'red' },
      { label: 'Invalid', value: summary.invalid, tone: 'red' },
    ].filter((s) => s.value > 0);
  }, [summary]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="bulk-mapping-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Bulk Sales Mapping Import
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="bg-[#0a0a0a] border border-[#27272a] rounded-lg p-4 text-xs text-zinc-400 space-y-2">
              <p>
                Upload a CSV or XLSX file mapping POS items to recipes. Columns:
              </p>
              <ul className="list-disc list-inside text-zinc-500 space-y-0.5">
                <li><code className="text-zinc-300">item_name</code> <span className="text-zinc-600">(required)</span></li>
                <li><code className="text-zinc-300">recipe_name</code> <span className="text-zinc-600">(or recipe_id — at least one required)</span></li>
                <li><code className="text-zinc-300">recipe_id</code> <span className="text-zinc-600">(use when names are ambiguous)</span></li>
                <li><code className="text-zinc-300">item_id</code>, <code className="text-zinc-300">item_category</code> <span className="text-zinc-600">(optional)</span></li>
              </ul>
              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => downloadTemplate('csv')} data-testid="bulk-import-template-csv">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV template
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadTemplate('xlsx')} data-testid="bulk-import-template-xlsx">
                  <Download className="w-3.5 h-3.5 mr-1" /> Excel template
                </Button>
              </div>
            </div>

            <div className="border-2 border-dashed border-[#3f3f46] rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFile}
                className="hidden"
                id="bulk-import-file-input"
                data-testid="bulk-import-file-input"
              />
              <label htmlFor="bulk-import-file-input" className="cursor-pointer flex flex-col items-center gap-2">
                <FileText className="w-10 h-10 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-300">
                  {parsedRows.length ? `${parsedRows.length} rows parsed` : 'Click to upload CSV or XLSX'}
                </span>
                <span className="text-xs text-zinc-500">Headers detected: {headers.join(', ') || '—'}</span>
              </label>
            </div>

            {parsedRows.length > 0 && (
              <>
                <label className="flex items-center gap-2 px-3 py-2 bg-[#0a0a0a] border border-[#27272a] rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoPublish}
                    onChange={(e) => setAutoPublish(e.target.checked)}
                    className="rounded"
                    data-testid="bulk-import-auto-publish"
                  />
                  <span className="text-sm text-zinc-300">
                    Auto-publish DRAFT recipes when mapping
                  </span>
                  <span className="text-xs text-zinc-500 ml-auto">
                    DRAFT rows will be blocked unless this is enabled.
                  </span>
                </label>

                <div className="flex items-center justify-end gap-2">
                  <Button variant="outline" onClick={runPreview} disabled={importing} data-testid="bulk-import-preview">
                    {importing ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}
                    Preview (dry run)
                  </Button>
                  <Button onClick={runImport} disabled={importing} data-testid="bulk-import-apply">
                    {importing ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    Import {parsedRows.length} rows
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {result && summary && (
          <div className="space-y-4" data-testid="bulk-import-result">
            <div className="flex items-center gap-2 text-sm">
              {importResult ? (
                <span className="text-emerald-400 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" /> Import complete
                </span>
              ) : (
                <span className="text-blue-400 inline-flex items-center gap-1">
                  <FileText className="w-4 h-4" /> Preview (dry run) — no writes yet
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {summaryRows.map((s) => (
                <div key={s.label} className={cn('px-3 py-2 rounded-lg border', TONE_CLASSES[s.tone])}>
                  <p className="text-[10px] uppercase tracking-wide opacity-70">{s.label}</p>
                  <p className="text-xl font-mono font-bold">{s.value}</p>
                </div>
              ))}
            </div>

            <div className="max-h-80 overflow-y-auto bg-[#0a0a0a] border border-[#27272a] rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-[#18181b] sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 text-zinc-400 font-medium">#</th>
                    <th className="px-3 py-2 text-zinc-400 font-medium">Item</th>
                    <th className="px-3 py-2 text-zinc-400 font-medium">Recipe</th>
                    <th className="px-3 py-2 text-zinc-400 font-medium">Status</th>
                    <th className="px-3 py-2 text-zinc-400 font-medium">Message</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272a]">
                  {(result.rows || []).map((r) => {
                    const tone = STATUS_TONE[r.status] || { tone: 'zinc', label: r.status };
                    return (
                      <tr key={r.row} data-testid={`bulk-import-row-${r.row}`}>
                        <td className="px-3 py-2 text-zinc-600 font-mono">{r.row}</td>
                        <td className="px-3 py-2 text-zinc-200 truncate max-w-[160px]">{r.item_name || '—'}</td>
                        <td className="px-3 py-2 text-zinc-300 truncate max-w-[160px]">
                          {r.recipe_name || r.recipe_id || '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={cn('inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold border', TONE_CLASSES[tone.tone])}>
                            {tone.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-zinc-400 truncate max-w-[280px]" title={r.message}>{r.message}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => { setPreviewResult(null); setImportResult(null); }} data-testid="bulk-import-back">
                <X className="w-3.5 h-3.5 mr-1" />
                Back
              </Button>
              {previewResult && !importResult && (
                <Button onClick={runImport} disabled={importing} data-testid="bulk-import-confirm-after-preview">
                  {importing ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                  Confirm import
                </Button>
              )}
              {importResult && (
                <Button onClick={() => { onComplete?.(); handleClose(); }} data-testid="bulk-import-done">
                  Done
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BulkMappingImportDialog;
